import { AlertCircle, Building2, CheckCircle2, Download, FileSpreadsheet, KeyRound, LogOut, RefreshCw, ShieldCheck, UploadCloud, Users, WandSparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { analyzeWorkbook, apiGet, apiPost, createUpload, downloadJobResult, downloadTemplate, type ProcessingJob, type Role, type SessionUser, type WorkbookStructureReview } from "../lib/api";
import { confirmPasswordReset, getCurrentSession, requestPasswordReset, signIn, signOut } from "../lib/auth";

interface Organization {
  organizationId: string;
  name: string;
}

interface Client {
  clientId: string;
  organizationId: string;
  name: string;
}

interface UserProfile {
  userId: string;
  organizationId: string;
  email: string;
  role: Role;
  allowedClientIds: string[];
}

type NoticeType = "info" | "success" | "error";

export function App() {
  const [token, setToken] = useState("");
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loginMode, setLoginMode] = useState<"login" | "forgot" | "reset">("login");
  const [email, setEmail] = useState("murakami.k@insight-science.co.jp");
  const [password, setPassword] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [message, setMessage] = useState("ログインしてください。");
  const [noticeType, setNoticeType] = useState<NoticeType>("info");
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [jobs, setJobs] = useState<ProcessingJob[]>([]);
  const [clientId, setClientId] = useState("");
  const [fileName, setFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [structure, setStructure] = useState<WorkbookStructureReview | null>(null);
  const [columnMappings, setColumnMappings] = useState<Record<string, string>>({});
  const canCreateOrganization = user?.role === "SYSTEM_MASTER";
  const canManageUsers = user?.role === "SYSTEM_MASTER" || user?.role === "ORG_ADMIN";
  const canEdit = user?.role !== "VIEWER";

  function notify(type: NoticeType, text: string) {
    setNoticeType(type);
    setMessage(text);
  }

  useEffect(() => {
    getCurrentSession()
      .then((session) => {
        const jwt = session?.getIdToken().getJwtToken();
        if (jwt) return boot(jwt);
        return undefined;
      })
      .catch(() => undefined);
  }, []);

  async function boot(jwt: string) {
    setToken(jwt);
    const me = await apiGet<SessionUser>("/me", jwt);
    setUser(me);
    await refreshData(jwt, me);
    notify("success", `${me.email} としてログイン中`);
  }

  async function refreshData(jwt = token, me = user) {
    if (!jwt || !me) return;
    const [clientRows, userRows, jobRows] = await Promise.all([
      apiGet<Client[]>("/clients", jwt),
      apiGet<UserProfile[]>("/users", jwt),
      apiGet<ProcessingJob[]>("/jobs", jwt)
    ]);
    setClients(clientRows);
    setUsers(userRows);
    setJobs(jobRows);
    if (me.role === "SYSTEM_MASTER") setOrganizations(await apiGet<Organization[]>("/organizations", jwt));
    if (!clientId && clientRows[0]) setClientId(clientRows[0].clientId);
  }

  async function onLogin(event: React.FormEvent) {
    event.preventDefault();
    try {
      const session = await signIn(email, password);
      await boot(session.getIdToken().getJwtToken());
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "ログインに失敗しました。");
    }
  }

  async function onRequestPasswordReset(event: React.FormEvent) {
    event.preventDefault();
    try {
      await requestPasswordReset(email);
      setLoginMode("reset");
      notify("success", "確認コードを送信しました。メールに届いたコードと新しいパスワードを入力してください。");
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "パスワード再設定メールの送信に失敗しました。");
    }
  }

  async function onConfirmPasswordReset(event: React.FormEvent) {
    event.preventDefault();
    try {
      await confirmPasswordReset(email, resetCode, newPassword);
      setLoginMode("login");
      setPassword("");
      setResetCode("");
      setNewPassword("");
      notify("success", "パスワードを更新しました。新しいパスワードでログインしてください。");
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "パスワード更新に失敗しました。");
    }
  }

  function onLogout() {
    signOut();
    setToken("");
    setUser(null);
    notify("info", "ログアウトしました。");
  }

  async function onCreateOrganization(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      const formElement = event.currentTarget;
      const form = new FormData(formElement);
      const created = await apiPost<Organization>("/organizations", token, { name: form.get("name") });
      formElement.reset();
      await refreshData();
      notify("success", `組織「${created.name}」を作成しました。`);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "組織作成に失敗しました。");
    }
  }

  async function onCreateClient(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      const formElement = event.currentTarget;
      const form = new FormData(formElement);
      const created = await apiPost<Client>("/clients", token, {
        organizationId: form.get("organizationId"),
        name: form.get("name")
      });
      formElement.reset();
      await refreshData();
      setClientId(created.clientId);
      notify("success", `クライアント「${created.name}」を作成しました。`);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "クライアント作成に失敗しました。");
    }
  }

  async function onCreateUser(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      const formElement = event.currentTarget;
      const form = new FormData(formElement);
      const created = await apiPost<UserProfile>("/users", token, {
        email: form.get("email"),
        organizationId: form.get("organizationId"),
        role: form.get("role"),
        allowedClientIds: String(form.get("allowedClientIds") || "")
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean),
        temporaryPassword: form.get("temporaryPassword") || undefined
      });
      formElement.reset();
      await refreshData();
      notify("success", `ユーザー「${created.email}」を作成しました。`);
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "ユーザー作成に失敗しました。");
    }
  }

  async function onCreateUpload(event: React.FormEvent) {
    event.preventDefault();
    if (!clientId || !selectedFile) return notify("error", "クライアントとExcelファイルを選択してください。");
    try {
      notify("info", "ファイルをアップロードし、仕訳処理を開始しています。");
      const result = await createUpload(clientId, selectedFile, token);
      await refreshData();
      notify(
        result.status === "FAILED" ? "error" : "success",
        `処理ジョブ ${result.jobId} は ${result.status} です。${result.message ?? ""}`
      );
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "処理ジョブ作成に失敗しました。");
    }
  }

  async function onAnalyzeWorkbook() {
    if (!clientId || !selectedFile) return notify("error", "クライアントとExcelファイルを選択してください。");
    try {
      const result = await analyzeWorkbook(clientId, selectedFile, token);
      setStructure(result);
      setColumnMappings(result.analysis.columnMappings);
      notify("success", "ファイル構造を読み取りました。推定内容を確認し、必要に応じて対応列を修正してください。");
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "ファイル構造の読み取りに失敗しました。");
    }
  }

  async function onDownloadTemplate() {
    if (!clientId) return notify("error", "クライアントを選択してください。");
    try {
      await downloadTemplate(clientId, token);
      notify("success", "テンプレートをダウンロードしました。");
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "テンプレートのダウンロードに失敗しました。");
    }
  }

  async function onDownloadJobResult(job: ProcessingJob) {
    if (!job.outputKey) return notify("error", "処理結果ファイルはまだ作成されていません。");
    try {
      await downloadJobResult(job.jobId, token);
      notify("success", "処理結果をダウンロードしました。");
    } catch (error) {
      notify("error", error instanceof Error ? error.message : "処理結果のダウンロードに失敗しました。");
    }
  }

  const organizationOptions = useMemo(() => {
    if (organizations.length > 0) return organizations;
    return user ? [{ organizationId: user.organizationId, name: user.organizationId }] : [];
  }, [organizations, user]);

  if (!user) {
    return (
      <main className="login-shell">
        <form
          className="login-card"
          onSubmit={loginMode === "forgot" ? onRequestPasswordReset : loginMode === "reset" ? onConfirmPasswordReset : onLogin}
        >
          <div className="brand dark">
            <img src="/assets/shiwake-ait-logo.png" alt="仕訳AIT" />
          </div>
          <label>
            メールアドレス
            <input value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          {loginMode === "login" && (
            <label>
              パスワード
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </label>
          )}
          {loginMode === "reset" && (
            <>
              <label>
                確認コード
                <input value={resetCode} onChange={(e) => setResetCode(e.target.value)} />
              </label>
              <label>
                新しいパスワード
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
              </label>
            </>
          )}
          <button className="primary" type="submit">
            <KeyRound size={18} />
            {loginMode === "forgot" ? "再設定メールを送信" : loginMode === "reset" ? "パスワードを更新" : "ログイン"}
          </button>
          {loginMode === "login" ? (
            <button className="text-button" type="button" onClick={() => setLoginMode("forgot")}>パスワードを忘れた方はこちら</button>
          ) : (
            <button className="text-button" type="button" onClick={() => setLoginMode("login")}>ログイン画面に戻る</button>
          )}
          <div className={`message-box ${noticeType}`}>
            {noticeType === "error" ? <AlertCircle size={18} /> : <CheckCircle2 size={18} />}
            <output>{message}</output>
          </div>
        </form>
      </main>
    );
  }

  return (
    <main className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <img src="/assets/shiwake-ait-logo.png" alt="仕訳AIT" />
        </div>
        <nav>
          <button className="nav-item active"><UploadCloud size={18} />処理</button>
          <button className="nav-item"><WandSparkles size={18} />ルール</button>
          {canManageUsers && <button className="nav-item"><Users size={18} />ユーザー</button>}
          <button className="nav-item"><ShieldCheck size={18} />権限</button>
        </nav>
      </aside>
      <section className="content">
        <header className="page-header">
          <div>
            <h1>Excel処理</h1>
            <p>{user.email} / {user.role} / org: {user.organizationId}</p>
          </div>
          <button className="secondary" onClick={onLogout}><LogOut size={18} />ログアウト</button>
        </header>

        <div className={`message-box ${noticeType}`}>
          {noticeType === "error" ? <AlertCircle size={18} /> : <CheckCircle2 size={18} />}
          <output>{message}</output>
        </div>

        <form className="upload-panel" onSubmit={onCreateUpload}>
          <label>
            クライアント
            <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">選択してください</option>
              {clients.map((client) => <option key={client.clientId} value={client.clientId}>{client.name}</option>)}
            </select>
          </label>
          <label>
            Excelファイル
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => {
                const file = e.currentTarget.files?.[0] ?? null;
                setSelectedFile(file);
                setFileName(file?.name ?? "");
                setStructure(null);
              }}
            />
          </label>
          <button className="secondary" disabled={!canEdit} type="button" onClick={onAnalyzeWorkbook}><FileSpreadsheet size={18} />構造確認</button>
          <button className="primary" disabled={!canEdit} type="submit"><UploadCloud size={18} />処理ジョブ作成</button>
          <button className="download-link" type="button" onClick={onDownloadTemplate}>
            <Download size={18} />テンプレート
          </button>
        </form>

        <section className="workflow">
          <div className="section-header">
            <h2>処理ジョブ一覧</h2>
            <button className="secondary compact" type="button" onClick={() => refreshData()}>
              <RefreshCw size={16} />更新
            </button>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>作成日時</th>
                  <th>ファイル名</th>
                  <th>クライアント</th>
                  <th>ステータス</th>
                  <th>メッセージ</th>
                  <th>結果</th>
                </tr>
              </thead>
              <tbody>
                {jobs.length === 0 ? (
                  <tr><td colSpan={6}>まだ処理ジョブはありません。</td></tr>
                ) : jobs.map((job) => (
                  <tr key={job.jobId}>
                    <td>{formatDateTime(job.createdAt)}</td>
                    <td title={job.fileName}>{job.fileName}</td>
                    <td>{clientName(job.clientId, clients)}</td>
                    <td><span className={`status ${job.status.toLowerCase()}`}>{job.status}</span></td>
                    <td title={job.message || job.jobId}>{job.message || job.jobId}</td>
                    <td>
                      <button
                        className="secondary compact"
                        type="button"
                        disabled={!job.outputKey}
                        onClick={() => onDownloadJobResult(job)}
                      >
                        <Download size={16} />結果
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {structure && (
          <section className="workflow structure-review">
            <h2>今回読み取れたファイル構造</h2>
            <div className="structure-summary">
              <span>仕訳対象: <strong>{structure.analysis.targetSheetName || "未判定"}</strong></span>
              <span>過去仕訳: <strong>{structure.analysis.historySheetName || "未検出"}</strong></span>
              <span>処理ルール: <strong>{structure.analysis.ruleSheetName || "未検出"}</strong></span>
              <span>名寄せ辞書: <strong>{structure.analysis.aliasSheetName || "未検出"}</strong></span>
            </div>
            <div className="mapping-grid">
              {["transactionDate", "description", "amountOut", "amountIn", "balance"].map((field) => (
                <label key={field}>
                  {field}
                  <select
                    value={columnMappings[field] ?? ""}
                    onChange={(e) => setColumnMappings((current) => ({ ...current, [field]: e.target.value }))}
                  >
                    <option value="">未指定</option>
                    {structure.sheets.find((sheet) => sheet.name === structure.analysis.targetSheetName)?.headers.map((header) => (
                      <option key={header} value={header}>{header}</option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
            <div className="sheet-list">
              {structure.sheets.map((sheet) => (
                <article key={sheet.name} className="sheet-preview">
                  <h3>{sheet.name} <code>{sheet.role}</code></h3>
                  <div className="sheet-meta">
                    <span>ヘッダ行: {sheet.headerRowNumber ?? "-"}</span>
                    <span>データ開始行: {sheet.dataStartRowNumber ?? "-"}</span>
                    <span>データ行数: {sheet.nonEmptyRowCount ?? 0}</span>
                  </div>
                  <p>{sheet.headers.join(" / ") || "ヘッダを検出できませんでした。"}</p>
                  {sheet.sampleRows[0] && (
                    <pre>{JSON.stringify(sheet.sampleRows[0], null, 2)}</pre>
                  )}
                </article>
              ))}
            </div>
          </section>
        )}

        <section className="grid">
          {canCreateOrganization && (
            <form className="panel" onSubmit={onCreateOrganization}>
              <h2><Building2 size={18} />組織作成</h2>
              <input name="name" placeholder="組織名" required />
              <button className="primary" type="submit">作成</button>
            </form>
          )}

          {canManageUsers && (
            <form className="panel" onSubmit={onCreateClient}>
              <h2>クライアント作成</h2>
              <select name="organizationId" required>
                {organizationOptions.map((org) => <option key={org.organizationId} value={org.organizationId}>{org.name}</option>)}
              </select>
              <input name="name" placeholder="クライアント名" required />
              <button className="primary" type="submit">作成</button>
            </form>
          )}

          {canManageUsers && (
            <form className="panel" onSubmit={onCreateUser}>
              <h2>ユーザー作成</h2>
              <input name="email" placeholder="email@example.com" required />
              <select name="organizationId" required>
                {organizationOptions.map((org) => <option key={org.organizationId} value={org.organizationId}>{org.name}</option>)}
              </select>
              <select name="role" required>
                {(["ORG_ADMIN", "EDITOR", "VIEWER"] as Role[]).map((role) => <option key={role} value={role}>{role}</option>)}
                {user.role === "SYSTEM_MASTER" && <option value="SYSTEM_MASTER">SYSTEM_MASTER</option>}
              </select>
              <input name="allowedClientIds" placeholder="許可クライアントID（カンマ区切り）" />
              <input name="temporaryPassword" placeholder="初期パスワード（任意）" />
              <button className="primary" type="submit">作成</button>
            </form>
          )}
        </section>

        <section className="workflow">
          <h2>閲覧可能クライアント</h2>
          <ul>
            {clients.map((client) => <li key={client.clientId}>{client.name} <code>{client.clientId}</code></li>)}
          </ul>
          {canManageUsers && <p>ユーザー数: {users.length}</p>}
        </section>
      </section>
    </main>
  );
}

function clientName(clientId: string, clients: Client[]) {
  return clients.find((client) => client.clientId === clientId)?.name ?? clientId;
}

function formatDateTime(value: string) {
  if (!value) return "";
  return new Intl.DateTimeFormat("ja-JP", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(value));
}
